import { ChevronRight } from '@1debit/icons';
import Button from 'components/shared/Buttons/BaseButton';
import ChimeKitIcon from 'components/shared/LinkWithIcon/ChimeKitIcon';
import Media from 'components/shared/Media/Media';
import NavLink from 'components/shared/NavLink/NavLink';
import { NavSubLinkProps } from './types';
import { cn } from 'utils/styles';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { generateIdSegmentFromLink, subLinkHasNestedItems } from './utils';

interface NavSubMenuNestedLinkProps {
  sublink: NavSubLinkProps;
  idPrefix: string;
  enableNestedDrillIn?: boolean;
  onNestedSubLinkClick?: (subLink: NavSubLinkProps) => void;
}

const NavSubMenuNestedLink = ({
  sublink,
  idPrefix,
  enableNestedDrillIn,
  onNestedSubLinkClick,
}: NavSubMenuNestedLinkProps) => {
  const { sys: subLinkSys, link, description, chimeKitIconName, icon } = sublink;
  if (!link) return null;

  const { sys: linkSys, label, url, internalPage } = link;
  const showIcon = chimeKitIconName != null || icon?.url != null;
  const subLinkIdSegment = generateIdSegmentFromLink(link);
  const hasNestedItems = subLinkHasNestedItems(sublink);

  // Entries may carry both fields; desktop picks one, so rendering both would stack two icons.
  const iconElement = (
    <div className="w-6 flex-none">
      {chimeKitIconName ? (
        <ChimeKitIcon iconName={chimeKitIconName} />
      ) : (
        icon && <Media media={{ ...icon, width: 24, height: 24 }} className="w-full" />
      )}
    </div>
  );

  if (enableNestedDrillIn && hasNestedItems && onNestedSubLinkClick) {
    return (
      <Button
        variant="navMenu"
        className="active:scale-none group w-full text-left"
        onClick={() => onNestedSubLinkClick(sublink)}>
        <div className={cn('flex gap-2 w-full', !description && 'items-center')} id={`${idPrefix}_${subLinkIdSegment}`}>
          {showIcon && iconElement}
          <div className="flex flex-1 items-center justify-between ml-2">
            <p className="font-sans text-label-saans font-semibold" {...liveUpdateAttrs(linkSys?.id, 'label')}>
              {label}
            </p>
            <ChevronRight className="size-(--ck-icon-size) text-fern" />
          </div>
        </div>
      </Button>
    );
  }

  return (
    <NavLink
      id={`${idPrefix}_${subLinkIdSegment}`}
      href={url}
      internalPage={internalPage}
      level={1}
      cleanLinkText={label}
      className="active:scale-none group hover:text-current">
      <div className={cn('flex gap-2', !description && 'items-center')}>
        {showIcon && iconElement}
        <div className="flex flex-col ml-2">
          <p
            className="font-sans text-label-saans font-semibold group-hover:text-fern group-focus:text-fern"
            {...liveUpdateAttrs(linkSys?.id, 'label')}>
            {label}
          </p>
          {description && (
            <p className="font-serif text-sm" {...liveUpdateAttrs(subLinkSys?.id, 'description')}>
              {description}
            </p>
          )}
        </div>
      </div>
    </NavLink>
  );
};

export default NavSubMenuNestedLink;
